import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutGrid, Inbox, Users, Calendar, Camera, Images,
  FileSignature, Receipt, Backpack, Scissors, BarChart3, Settings, Aperture,
} from "lucide-react";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
} from "@/components/ui/sidebar";
import { studio } from "@/mocks/data";

type Item = { title: string; url: string; icon: typeof LayoutGrid };

const pipeline: Item[] = [
  { title: "Dashboard", url: "/", icon: LayoutGrid },
  { title: "Inquiries", url: "/leads", icon: Inbox },
  { title: "Clients", url: "/clients", icon: Users },
  { title: "Bookings", url: "/bookings", icon: Calendar },
];
const production: Item[] = [
  { title: "Shoots", url: "/shoots", icon: Camera },
  { title: "Galleries", url: "/galleries", icon: Images },
  { title: "Editing", url: "/editing", icon: Scissors },
  { title: "Gear", url: "/gear", icon: Backpack },
];
const finance: Item[] = [
  { title: "Contracts", url: "/contracts", icon: FileSignature },
  { title: "Invoices", url: "/invoices", icon: Receipt },
  { title: "Reports", url: "/reports", icon: BarChart3 },
];
const system: Item[] = [{ title: "Settings", url: "/settings", icon: Settings }];

function Section({ label, items, pathname }: { label: string; items: Item[]; pathname: string }) {
  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
        {label}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const active = item.url === "/" ? pathname === "/" : pathname.startsWith(item.url);
            return (
              <SidebarMenuItem key={item.url}>
                <SidebarMenuButton asChild isActive={active}>
                  <Link to={item.url} className="flex items-center gap-2">
                    <item.icon className="size-4" />
                    <span className="font-medium">{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

export function AppSidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      <SidebarHeader className="p-4">
        <Link to="/" className="flex items-center gap-3 px-2 py-1">
          <div className="flex size-8 flex-none items-center justify-center rounded-md bg-foreground text-background">
            <Aperture className="size-4" />
          </div>
          <div className="flex flex-col leading-tight group-data-[collapsible=icon]:hidden">
            <span className="text-sm font-semibold tracking-tight">{studio.name}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Studio OS · v1
            </span>
          </div>
        </Link>
      </SidebarHeader>
      <SidebarContent className="px-2">
        <Section label="Pipeline" items={pipeline} pathname={pathname} />
        <Section label="Production" items={production} pathname={pathname} />
        <Section label="Finance" items={finance} pathname={pathname} />
        <Section label="System" items={system} pathname={pathname} />
      </SidebarContent>
    </Sidebar>
  );
}
